<script>
import { mapStores } from "pinia";
import { defineComponent } from "vue";
import { RouterLink } from "vue-router";

import TgIconButton from "@/abc-dsk/components/IconButton/TgIconButton.vue";
import RouteHelper from "@/common/route-helper";
import IconWhiteDocumentNoBorder from "@/components/icons/white/IconWhiteDocumentNoBorder.vue";
import { QUERY_INFLECTION_SCHEME_ID_KEY } from "@/router/query-params";
import { useInflectionSchemeStore } from "@/stores";

export default defineComponent({
  name: "InflectionSchemeLink",
  components: {
    IconWhiteDocumentNoBorder,
    TgIconButton,
    RouterLink
  },
  props: {
    word: {
      type: [String, null],
      default: null
    },
    articleId: {
      type: [String, Number],
      required: true
    },
    translationId: {
      type: [String, Number],
      default: undefined
    },
    sectionId: {
      type: [String, Number],
      default: undefined
    },
    iconClass: {
      type: [String, Array, Object],
      default: undefined
    }
  },
  emits: ["mouseover", "mouseout"],
  data() {
    return {
      // The activeTrigger property ensures that the isInflectionSchemeOpen watcher doesn't set focus for every single button.
      activeTrigger: undefined
    };
  },
  computed: {
    ...mapStores(useInflectionSchemeStore),

    inflectionSchemeId() {
      return this["inflection-schemeStore"].inflectionSchemeId;
    },
    isInflectionSchemeOpen() {
      return this["inflection-schemeStore"].isInflectionSchemeOpen;
    },

    id() {
      return `${this.articleId}_${this.translationId || ""}_${this.sectionId || ""}`;
    }
  },
  watch: {
    isInflectionSchemeOpen(newValue, oldValue) {
      if (newValue === true || newValue === oldValue) {
        return;
      }

      if (!this.activeTrigger) {
        return;
      }

      if (Array.isArray(this.activeTrigger)) {
        this.activeTrigger = this.activeTrigger.pop();
      }

      // Restore focus to inflection scheme button, if not opened through hyperlink
      this.activeTrigger.focus({ preventScroll: true });
      this.activeTrigger = undefined;
    }
  },
  methods: {
    getInflectionSchemeQueryParam(inflectionSchemeId) {
      const routeHelper = new RouteHelper(this.$router);
      return routeHelper.getQueryParams({
        [QUERY_INFLECTION_SCHEME_ID_KEY]: inflectionSchemeId
      });
    },
    onOpen(e, cb) {
      this.activeTrigger = this.$refs["inflection-scheme-trigger"].$el;
      cb(e);
    }
  }
});
</script>

<template>
  <RouterLink
    v-slot="{ navigate }"
    custom
    :to="{
      name: 'article',
      query: getInflectionSchemeQueryParam(id)
    }"
  >
    <TgIconButton
      ref="inflection-scheme-trigger"
      v-bind="$attrs"
      aria-haspopup="true"
      :aria-label="
        $t('article.inflectionScheme.open', {
          word: word
        })
      "
      @mouseover="$emit('mouseover', $event)"
      @mouseout="$emit('mouseout', $event)"
      @click.stop="onOpen($event, navigate)"
    >
      <IconWhiteDocumentNoBorder :class="iconClass" role="presentation"></IconWhiteDocumentNoBorder>
    </TgIconButton>
  </RouterLink>
</template>
